import React, { useState, useEffect, useCallback } from 'react';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import SyntaxHighlighter from 'react-syntax-highlighter';

const excludedEngineStructures = new Set([
  "FName", "FVector", "FVector2D", "FVector4", "FRotator", "FQuat", "FTransform",
  "FColor", "FLinearColor", "FBox", "FBox2D", "FSphere", "FMatrix", "FDateTime",
  "FTimespan", "FText", "FString", "FStringAssetReference", "FSoftObjectPath",
  "FSoftClassPath", "FGameplayTag", "FMulticastInlineDelegateProperty_",
  "FGameplayTagContainer", "FMarkedActorDisplayInfo", "FVector3f",
  "FPointerToUberGraphFrame", "FGuid", "FSlateBrush", "FTimerHandle",
  "FCurveTableRowHandle", "FPostProcessSettings", "FRotator3f", "FVector2f",
  "FLightingChannels", "FScalableFloat", "FVectorRK4SpringInterpolator",
  "FTimeline", "FGameplayAbilityRepAnimMontage"
]);

const KeywordsToIncludes = {
  "AActor": "#include <GameFramework/Actor.h>",
  "UStaticMeshComponent": "#include <Components/StaticMeshComponent.h>",
  "USkeletalMeshComponent": "#include <Components/SkeletalMeshComponent.h>",
  "UWidgetComponent": "#include <Components/WidgetComponent.h>",
  "APawn": "#include <GameFramework/Pawn.h>",
  "ACharacter": "#include <GameFramework/Character.h>",
  "UAnimInstance": "#include <Animation/AnimInstance.h>",
  "UGameplayStatics": "#include <Kismet/GameplayStatics.h>",
  "UUserWidget": "#include <Blueprint/UserWidget.h>",
  "UNavMeshBoundsVolume": "#include <NavigationSystem.h>",
  "AAIController": "#include <AIController.h>",
  "UBehaviorTree": "#include <BehaviorTree/BehaviorTree.h>",
  "UBlackboardComponent": "#include <BehaviorTree/BlackboardComponent.h>",
  "UCharacterMovementComponent": "#include <GameFramework/CharacterMovementComponent.h>",
  "UParticleSystemComponent": "#include <Particles/ParticleSystemComponent.h>",
  "UAudioComponent": "#include <Components/AudioComponent.h>",
  "UMaterialInterface": "#include <Materials/MaterialInterface.h>",
  "UTexture2D": "#include <Engine/Texture2D.h>",
  "UPhysicsHandleComponent": "#include <PhysicsEngine/PhysicsHandleComponent.h>",
  "UProjectileMovementComponent": "#include <GameFramework/ProjectileMovementComponent.h>",
  "USaveGame": "#include <GameFramework/SaveGame.h>",
  "ULevelStreaming": "#include <Engine/LevelStreaming.h>",
  "FGameplayTag": "#include <GameplayTagContainer.h>",
  "FGameplayTagContainer": "#include <GameplayTagContainer.h>",
  "UGameplayAbility": "#include <Abilities/GameplayAbility.h>",
  "UCurveFloat": "#include <Curves/CurveFloat.h>",
  "FScalableFloat": "#include <AttributeSet.h>",
  "UBoxComponent": "#include <Components/BoxComponent.h>",
  "FSlateBrush": "#include <Styling/SlateBrush.h>",
  "FTimeline": "#include <Components/TimelineComponent.h>",
  "UPostProcessComponent": "#include <Components/PostProcessComponent.h>",
  "UNiagaraSystem": "#include <NiagaraSystem.h>",
  "UGameplayEffect": "#include <GameplayEffect.h>",
  "UWidget": "#include <Components/Widget.h>",
  "FLightingChannels": "#include <Engine/EngineTypes.h>",
  "FGameplayAbilityRepAnimMontage": "#include <Abilities/GameplayAbilityTypes.h>",
  "FVectorRK4SpringInterpolator": "#include <Engine/SpringInterpolator.h>"
};

const UnrealClassViewer = ({ dataUrl, previousView, onReturn }) => {
  const [classData, setClassData] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const adjustVariableType = useCallback((variableType) => {
    return variableType
      .replace(/\b(class|const|struct)\b\s*/g, '')
      .replace(/u?int(8|16|32|64)?/gi, 'int')
      .trim();
  }, []);

  const extractGameNameFromUrl = useCallback((url) => {
    const regex = /https:\/\/cdn\.spookytools\.com\/Games\/([^/]+)\/Classes\/[^/]+\.json/;
    const match = url?.match(regex);
    return match ? match[1] : null;
  }, []);

  const fetchAndAppendStructs = useCallback(async (structsToFetch, dataurl) => {
    let fetchedStructsContent = '';
    const gameName = extractGameNameFromUrl(dataurl);
    
    for (const structName of structsToFetch) {
      try {
        const response = await fetch(
          `https://cdn.spookytools.com/Games/${gameName}/Structures/${structName}.json`
        );
        if (!response.ok) continue;
        const structData = await response.json();
        
        let structContent = `USTRUCT(BlueprintType)\nstruct ${structName}\n{\n  GENERATED_BODY()\npublic:\n`;
        structData.variables.forEach(variable => {
          structContent += `  UPROPERTY(BlueprintReadWrite, EditAnywhere)\n  ${adjustVariableType(variable.type)} ${variable.name};\n`;
        });
        structContent += '};\n\n';
        fetchedStructsContent += structContent;
      } catch (error) {
        console.warn(`Error fetching structure for ${structName}:`, error);
      }
    }
    return fetchedStructsContent;
  }, [extractGameNameFromUrl, adjustVariableType]);

  const fetchAndAppendEnums = useCallback(async (enumsToFetch, dataurl) => {
    let fetchedEnumsContent = '';
    const gameName = extractGameNameFromUrl(dataurl);
    
    for (const enumName of enumsToFetch) {
      try {
        const response = await fetch(
          `https://cdn.spookytools.com/Games/${gameName}/Enums/${enumName}.json`
        );
        if (!response.ok) continue;
        const enumData = await response.json();
        
        let enumContent = `UENUM(BlueprintType)\nenum class ${enumData.enumName} : uint8\n{\n`;
        enumData.values.forEach(variable => {
          enumContent += `  ${variable.name} = ${variable.value},\n`;
        });
        enumContent += '};\n\n';
        fetchedEnumsContent += enumContent;
      } catch (error) {
        console.warn(`Error fetching enum ${enumName}:`, error);
      }
    }
    return fetchedEnumsContent;
  }, [extractGameNameFromUrl]);

  const copyToClipboard = useCallback(() => {
    if (!classData) return;
    navigator.clipboard.writeText(classData.content.replace(/<[^>]+>/g, ''))
      .then(() => {
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      })
      .catch(err => console.error('Failed to copy:', err));
  }, [classData]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        copyToClipboard();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [copyToClipboard]);

  useEffect(() => {
    const fetchClassDetails = async () => {
      try {
        const response = await fetch(dataUrl);
        if (!response.ok) throw new Error('Failed to fetch class details');
        const data = await response.json();
        
        const trimmedClassName = data.className.replace(/_C$/, '')
          .replace(/\b(\w*?)[EAU](?=[A-Z]\w*\b)/g, '$1');

        let includesSet = new Set();
        let structsToFetch = [];
        let enumsToFetch = [];
        
        const addIncludeForType = (type) => {
          const normalizedType = type.replace(/[*&]/g, '').trim();
          const baseTypeMatch = normalizedType.match(/(?:class|struct|enum class)?\s*([^\s<]+)/);
          
          if (baseTypeMatch) {
            const baseType = baseTypeMatch[1];
            if (/^F[A-Z]/.test(baseType) && !excludedEngineStructures.has(baseType)) {
              structsToFetch.push(baseType);
            }
            if (/^E[A-Z]/.test(baseType)) {
              enumsToFetch.push(baseType);
            }
            Object.entries(KeywordsToIncludes).forEach(([keyword, include]) => {
              if (baseType.includes(keyword)) {
                includesSet.add(include);
              }
            });
          }
        };

        if (data.parentClassName.startsWith('A')) {
          includesSet.add(KeywordsToIncludes.AActor);
        } else {
          addIncludeForType(data.parentClassName);
        }

        data.variables.forEach(variable => addIncludeForType(variable.type));

        const [structsContent, enumsContent] = await Promise.all([
          fetchAndAppendStructs(structsToFetch, dataUrl),
          fetchAndAppendEnums(enumsToFetch, dataUrl)
        ]);

        const delegateVariables = data.variables.filter(v => v.type.includes('Multicast'));
        const nonDelegateVariables = data.variables.filter(v => !v.type.includes('Multicast'));

        const delegateDefinitions = delegateVariables
          .map(v => `\nDECLARE_DYNAMIC_MULTICAST_DELEGATE(${v.name});`)
          .join('\n');

        const classContent = [
          structsContent,
          enumsContent,
          delegateDefinitions,
          '\nUCLASS()',
          `class ${trimmedClassName} : public ${data.parentClassName}`,
          '{',
          '  GENERATED_BODY()',
          'public:',
          ...nonDelegateVariables.map(v => 
            `  UPROPERTY(BlueprintReadWrite, EditAnywhere)\n  ${adjustVariableType(v.type)} ${v.name};`
          ),
          data.functions.length ? '  // Functions' : '',
          ...data.functions
            .filter(f => !f.name.includes("ExecuteUbergraph"))
            .map(f => {
              const params = f.parameters
                .map(p => `${adjustVariableType(p.type)} ${p.name}`)
                .join(', ');
              return `  UFUNCTION(BlueprintCallable)\n  ${f.returnType} ${f.name}(${params});`;
            }),
          '};'
        ].join('\n');

        setClassData({
          className: trimmedClassName,
          content: classContent,
          includes: ['#include <CoreMinimal.h>', ...includesSet, `#include <${trimmedClassName}.generated.h>`]
        });
      } catch (error) {
        console.error('Failed to fetch class details:', error);
      }
    };

    if (dataUrl) {
      fetchClassDetails();
    }
  }, [dataUrl, adjustVariableType, fetchAndAppendStructs, fetchAndAppendEnums]);

  if (!classData) {
    return (
        <div className="bg-gray-900 flex items-center justify-center scale-100">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  const fullCode = `${classData.includes.join('\n')}\n\n${classData.content}`;

  return (
    <div className="bg-gray-900 text-gray-300 font-mono w-screen h-screen">
      <div className="p-2 bg-[#1E1E1E] border-b border-gray-700 flex justify-between items-center sticky top-0 z-50">
        <div className="text-m">
          {classData.className}
        </div>
        <div className="flex items-center space-x-4 text-m">
          <span className="text-gray-500">Ctrl+C to copy</span>
          <button 
            onClick={onReturn}
            className="px-2 py-0.5 bg-gray-700 rounded hover:bg-gray-600 transition-colors"
          >
            Back
          </button>
        </div>
      </div>
      
      <div className="overflow-auto h-[calc(100vh-40px)]">
        <SyntaxHighlighter
          language="cpp"
          style={vs2015}
          customStyle={{
            margin: 0,
            padding: '1rem',
            background: '#1E1E1E',
            fontSize: '20px',
          }}
          showLineNumbers
        >
          {fullCode}
        </SyntaxHighlighter>
      </div>

      {showToast && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-3 py-1 text-xs rounded shadow-lg border border-gray-700 transform transition-all duration-300 ease-in-out">
          Copied to clipboard
        </div>
      )}
    </div>
  );
};

export default UnrealClassViewer;